import {
  imageGenerationAgentRunMultiplier,
  termsPath,
} from "../../constants.ts";
import { usePageMeta } from "./pageMeta.ts";
import {
  freeTrialTerms,
  PricingSection,
  recurringPlanTerms,
  runExplanation,
} from "./pricing.tsx";
import { ComparisonSection } from "./featureComparison.tsx";
import { pricingSocialMeta } from "./socialMeta.ts";

const faqItems = [
  {
    q: "Can I try prompt2bot for free?",
    a: freeTrialTerms,
  },
  {
    q: "Why is phone verification needed for the higher free-tier limit?",
    a: "Phone verification helps prevent repeated free-account signups. The higher allowance requires verification with a WhatsApp PIN. You can try an agent on the web without connecting WhatsApp.",
  },
  {
    q: "What counts as an LLM run?",
    a: runExplanation,
  },
  {
    q: "Can I run recurring automations on the free plan?",
    a: recurringPlanTerms,
  },
  {
    q: "How much does image generation cost?",
    a: `Image generation costs ${imageGenerationAgentRunMultiplier} LLM runs per image because it is more computationally expensive to serve. This applies across all plans.`,
  },
  {
    q: "How does billing work?",
    a: "Paid plans are billed monthly via a recurring subscription. Your card is charged automatically on the same date each month. You can cancel at any time from your Settings page.",
  },
  {
    q: "Can I upgrade or downgrade at any time?",
    a: "Yes. You can change your plan at any time. When upgrading, the new rate applies immediately. When downgrading, the change takes effect at the start of your next billing cycle.",
  },
  {
    q: "What happens if I cancel?",
    a: "You retain access to your current plan's features until the end of the billing period. After that, your account reverts to the Free plan.",
  },
  {
    q: "Do you offer refunds?",
    a: "Yes — we offer a 14-day refund policy. See our Refund Policy page for full details.",
  },
];

const FaqItem = ({ q, a }: { q: string; a: string }) => (
  <div className="border-b border-border-subtle-light dark:border-border-subtle pb-4">
    <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary mb-1">
      {q}
    </h3>
    <p className="text-text-secondary-light dark:text-text-secondary text-sm">
      {a}
    </p>
  </div>
);

export const PricingPage = () => {
  usePageMeta(pricingSocialMeta);
  return (
    <div className="py-12">
      <h1 className="text-4xl font-display font-bold text-center text-text-primary-light dark:text-text-primary mb-8 px-4">
        AI agent pricing
      </h1>
      <div className="px-4 py-10 bg-surface-overlay-light/50 dark:bg-surface-raised/30 border-y border-border-subtle-light dark:border-border-subtle">
        <PricingSection />
      </div>
      <div className="max-w-5xl mx-auto px-4">
        <div className="mt-12 max-w-2xl mx-auto text-center text-sm text-text-tertiary-light dark:text-text-tertiary space-y-2">
          <p>All paid plans are billed monthly via recurring subscription.</p>
          <p>
            Prices are in USD. You can cancel anytime from your Settings page.
          </p>
          <p>
            By subscribing you agree to our{" "}
            <a
              href={termsPath}
              className="underline text-accent-light dark:text-accent"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/refund-policy"
              className="underline text-accent-light dark:text-accent"
            >
              Refund Policy
            </a>
            .
          </p>
        </div>
        <div className="mt-12">
          <ComparisonSection />
        </div>
        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center text-text-primary-light dark:text-text-primary">
            Frequently Asked Questions
          </h2>
          <div className="space-y-5">
            {faqItems.map(({ q, a }) => <FaqItem key={q} q={q} a={a} />)}
          </div>
        </div>
      </div>
    </div>
  );
};
